import { type RefObject, useState, useCallback } from "react";
import { api, type Snippet } from "../../api/client";

interface MarkdownToolbarProps {
  setMarkdown: (value: string) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
}

function insertAtCursor(
  textarea: HTMLTextAreaElement,
  setMarkdown: (v: string) => void,
  before: string,
  after: string,
  placeholder: string = "",
) {
  const { selectionStart, selectionEnd, value } = textarea;
  const selected = value.slice(selectionStart, selectionEnd);
  const text = selected || placeholder;
  const next =
    value.slice(0, selectionStart) +
    before +
    text +
    after +
    value.slice(selectionEnd);
  setMarkdown(next);
  // Restore focus and position cursor inside the inserted text
  requestAnimationFrame(() => {
    textarea.focus();
    const cursorPos =
      selectionStart +
      before.length +
      (selected ? text.length : placeholder.length);
    textarea.setSelectionRange(cursorPos, cursorPos);
  });
}

function prependLine(
  textarea: HTMLTextAreaElement,
  setMarkdown: (v: string) => void,
  prefix: string,
) {
  const { selectionStart, value } = textarea;
  // Find start of current line
  const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
  const lineEnd = value.indexOf("\n", selectionStart);
  const actualEnd = lineEnd === -1 ? value.length : lineEnd;
  const currentLine = value.slice(lineStart, actualEnd);

  // Check if the line already has this prefix (toggle off)
  let nextLine: string;
  if (currentLine.startsWith(prefix)) {
    nextLine = currentLine.slice(prefix.length);
  } else {
    // Remove any existing heading/list prefix before adding new one
    const stripped = currentLine.replace(/^(#{1,6}\s|[-*]\s|\d+\.\s|>\s)/, "");
    nextLine = prefix + stripped;
  }

  const next = value.slice(0, lineStart) + nextLine + value.slice(actualEnd);
  setMarkdown(next);
  requestAnimationFrame(() => {
    textarea.focus();
    const newPos = lineStart + nextLine.length;
    textarea.setSelectionRange(newPos, newPos);
  });
}

/** Replace the current selection with `text`, leaving the cursor after it. */
function insertText(
  textarea: HTMLTextAreaElement,
  setMarkdown: (v: string) => void,
  text: string,
) {
  const { selectionStart, selectionEnd, value } = textarea;
  const next =
    value.slice(0, selectionStart) + text + value.slice(selectionEnd);
  setMarkdown(next);
  requestAnimationFrame(() => {
    textarea.focus();
    const cursorPos = selectionStart + text.length;
    textarea.setSelectionRange(cursorPos, cursorPos);
  });
}

/**
 * "Insert Snippet" dropdown — lazily loads the snippet library from the docs
 * repo on first open and inserts the picked snippet at the cursor.
 */
function SnippetPicker({ onInsert }: { onInsert: (content: string) => void }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [snippets, setSnippets] = useState<Snippet[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = useCallback(() => {
    if (!open && snippets === null) {
      setError(null);
      api.snippets
        .list()
        .then(setSnippets)
        .catch((e) => {
          const detail =
            e instanceof Error ? e.message : "Failed to load snippets";
          setError(detail.includes("503") ? "GitHub not configured." : detail);
        });
    }
    setOpen(!open);
  }, [open, snippets]);

  return (
    <span style={styles.snippetWrap}>
      <button
        title="Insert snippet"
        onClick={toggle}
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          ...styles.snippetBtn,
          background: open
            ? "var(--comp-rule)"
            : hovered
              ? "var(--comp-hover)"
              : "transparent",
          color: open || hovered ? "var(--fg-1)" : "var(--fg-3)",
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0 }}
        >
          <circle cx="6" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <path d="M20 4 8.12 15.88" />
          <path d="M14.47 14.48 20 20" />
          <path d="M8.12 8.12 12 12" />
        </svg>
        snippet
      </button>

      {open && (
        <>
          <div
            style={styles.snippetBackdrop}
            onMouseDown={() => setOpen(false)}
          />
          <div style={styles.snippetMenu}>
            {error ? (
              <div style={styles.snippetStatus}>{error}</div>
            ) : snippets === null ? (
              <div style={styles.snippetStatus}>loading…</div>
            ) : snippets.length === 0 ? (
              <div style={styles.snippetStatus}>no snippets yet</div>
            ) : (
              snippets.map((s) => (
                <button
                  key={s.slug}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onInsert(s.content);
                    setOpen(false);
                  }}
                  style={styles.snippetItem}
                >
                  <span style={styles.snippetItemTitle}>
                    {s.title}
                    {s.category && (
                      <span style={styles.snippetItemCategory}>
                        {s.category}
                      </span>
                    )}
                  </span>
                  {s.description && (
                    <span style={styles.snippetItemDesc}>{s.description}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </span>
  );
}

function ToolbarButton({
  label,
  title,
  onClick,
}: {
  label: string;
  title: string;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); // prevent textarea blur
    setPressed(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setPressed(false);
  }, []);

  const style: React.CSSProperties = {
    ...styles.btn,
    background: pressed
      ? "var(--comp-rule)"
      : hovered
        ? "var(--comp-hover)"
        : "transparent",
    color: hovered || pressed ? "var(--fg-1)" : "var(--fg-3)",
  };

  return (
    <button
      title={title}
      onClick={onClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      style={style}
    >
      {label}
    </button>
  );
}

export function MarkdownToolbar({
  setMarkdown,
  textareaRef,
}: MarkdownToolbarProps) {
  const ta = () => {
    if (!textareaRef.current) throw new Error("textarea ref not attached");
    return textareaRef.current;
  };

  const onBold = () =>
    insertAtCursor(ta(), setMarkdown, "**", "**", "bold text");
  const onItalic = () =>
    insertAtCursor(ta(), setMarkdown, "*", "*", "italic text");
  const onH1 = () => prependLine(ta(), setMarkdown, "# ");
  const onH2 = () => prependLine(ta(), setMarkdown, "## ");
  const onH3 = () => prependLine(ta(), setMarkdown, "### ");
  const onBullet = () => prependLine(ta(), setMarkdown, "- ");
  const onNumbered = () => prependLine(ta(), setMarkdown, "1. ");
  const onCodeBlock = () =>
    insertAtCursor(ta(), setMarkdown, "```\n", "\n```", "code");
  const onLink = () =>
    insertAtCursor(ta(), setMarkdown, "[", "](url)", "link text");
  const onFormula = () =>
    insertAtCursor(ta(), setMarkdown, "$$\n", "\n$$", "formula");
  const onBlockquote = () => prependLine(ta(), setMarkdown, "> ");

  return (
    <div style={styles.toolbar}>
      <ToolbarButton label="B" title="Bold" onClick={onBold} />
      <ToolbarButton label="I" title="Italic" onClick={onItalic} />

      <span style={styles.divider} />

      <ToolbarButton label="H1" title="Heading 1" onClick={onH1} />
      <ToolbarButton label="H2" title="Heading 2" onClick={onH2} />
      <ToolbarButton label="H3" title="Heading 3" onClick={onH3} />

      <span style={styles.divider} />

      <ToolbarButton label="•" title="Bullet List" onClick={onBullet} />
      <ToolbarButton label="1." title="Numbered List" onClick={onNumbered} />
      <ToolbarButton label=">" title="Blockquote" onClick={onBlockquote} />

      <span style={styles.divider} />

      <ToolbarButton label="⟨⟩" title="Code Block" onClick={onCodeBlock} />
      <ToolbarButton label="Link" title="Insert Link" onClick={onLink} />
      <ToolbarButton label="∑" title="Formula" onClick={onFormula} />

      <span style={styles.divider} />

      <SnippetPicker
        onInsert={(content) => insertText(ta(), setMarkdown, content)}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    height: 37,
    boxSizing: "border-box",
    padding: "0 8px",
    background: "var(--comp-paper)",
    borderBottom: "1px solid var(--comp-rule)",
    flexShrink: 0,
  },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    border: "none",
    borderRadius: "var(--radius-sm)",
    background: "transparent",
    color: "var(--fg-3)",
    fontFamily: "var(--font-main)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    padding: 0,
    lineHeight: 1,
    transition: "background 0.12s, color 0.12s",
  },
  divider: {
    display: "inline-block",
    width: 1,
    height: 18,
    background: "var(--comp-rule)",
    margin: "0 4px",
    flexShrink: 0,
  },
  snippetWrap: {
    position: "relative" as const,
    display: "inline-flex",
  },
  snippetBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    height: 28,
    padding: "0 8px",
    border: "none",
    borderRadius: "var(--radius-sm)",
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    fontWeight: 500,
    cursor: "pointer",
    lineHeight: 1,
    whiteSpace: "nowrap" as const,
    transition: "background 0.12s, color 0.12s",
  },
  snippetBackdrop: {
    position: "fixed" as const,
    inset: 0,
    zIndex: 499,
  },
  snippetMenu: {
    position: "absolute" as const,
    top: "calc(100% + 4px)",
    left: 0,
    zIndex: 500,
    width: 280,
    maxHeight: 320,
    overflowY: "auto" as const,
    background: "var(--comp-paper)",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-md)",
    padding: 4,
    display: "flex",
    flexDirection: "column" as const,
    gap: 2,
  },
  snippetStatus: {
    padding: "10px 12px",
    fontSize: 12,
    color: "var(--fg-3)",
    lineHeight: 1.5,
  },
  snippetItem: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "stretch",
    gap: 2,
    padding: "8px 10px",
    border: "none",
    borderRadius: "var(--radius-sm)",
    background: "transparent",
    cursor: "pointer",
    textAlign: "left" as const,
    fontFamily: "var(--font-main)",
  },
  snippetItemTitle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    color: "var(--fg-1)",
  },
  snippetItemCategory: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    fontWeight: 500,
    color: "var(--fg-3)",
  },
  snippetItemDesc: {
    fontSize: 11,
    color: "var(--fg-3)",
    lineHeight: 1.4,
  },
};
